import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ActionButton, PremiumCard, PremiumEmpty, StatRing } from '../components/premium';
import { CollapsiblePanel } from '../components/premium/CollapsiblePanel';
import { NavIcon } from '../components/NavIcons';
import { api } from '../lib/api';
import { localDateKey } from '../lib/dateKey';
import { dateAndTimeToIso, isoToDateAndTime } from '../lib/datetimeLocal';
import { fieldControl } from '../lib/uiClasses';

type DaySleepSlice = {
  sleepHours: number | null;
  sleepBedAt: string | null;
  sleepWakeAt: string | null;
};

const sleepInputClass = `${fieldControl} text-base sm:text-sm`;

function sleepScore(hours: number | null): number {
  if (hours == null || hours <= 0) return 0;
  if (hours >= 7 && hours <= 9) return 92;
  if (hours >= 6) return 78;
  if (hours >= 5) return 62;
  return 45;
}

function formatTimeChip(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function SleepPage() {
  const [searchParams] = useSearchParams();
  const [dateKey, setDateKey] = useState(() => localDateKey());
  const [day, setDay] = useState<DaySleepSlice | null>(null);
  const [bedDate, setBedDate] = useState('');
  const [bedTime, setBedTime] = useState('');
  const [wakeDate, setWakeDate] = useState('');
  const [wakeTime, setWakeTime] = useState('');
  const [sleepHoursManual, setSleepHoursManual] = useState('');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    const q = searchParams.get('date');
    if (q && /^\d{4}-\d{2}-\d{2}$/.test(q)) setDateKey(q);
  }, [searchParams]);

  const applyFromDay = useCallback((d: DaySleepSlice) => {
    const bed = isoToDateAndTime(d.sleepBedAt);
    const wake = isoToDateAndTime(d.sleepWakeAt);
    setBedDate(bed.date);
    setBedTime(bed.time);
    const wakeFromApi = wake.date;
    const defaultWakeDay = d.sleepBedAt == null && d.sleepWakeAt == null && d.sleepHours == null ? dateKey : '';
    setWakeDate(wakeFromApi || defaultWakeDay);
    setWakeTime(wake.time);
    setSleepHoursManual(d.sleepBedAt && d.sleepWakeAt ? '' : d.sleepHours != null ? String(d.sleepHours) : '');
  }, [dateKey]);

  async function refresh() {
    const d = await api<DaySleepSlice>(`/api/nutrition/day/${dateKey}`);
    setDay(d);
    applyFromDay(d);
  }

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const d = await api<DaySleepSlice>(`/api/nutrition/day/${dateKey}`);
        if (!c) {
          setDay(d);
          applyFromDay(d);
        }
      } catch {
        if (!c) setDay(null);
      }
    })();
    return () => {
      c = true;
    };
  }, [dateKey, applyFromDay]);

  const hasCompleteBedWake = !!(bedDate.trim() && bedTime.trim() && wakeDate.trim() && wakeTime.trim());
  const hours = day?.sleepHours ?? (sleepHoursManual.trim() ? Number(sleepHoursManual) : null);
  const hasLog = hours != null && hours > 0;

  async function saveSleep() {
    setSaving(true);
    setNote(null);
    try {
      const hasManual = sleepHoursManual.trim() !== '';
      let body: Record<string, unknown>;
      if (hasCompleteBedWake) {
        const b = dateAndTimeToIso(bedDate, bedTime);
        const w = dateAndTimeToIso(wakeDate, wakeTime);
        if (!b || !w) {
          setNote('Check bed and wake times.');
          setSaving(false);
          return;
        }
        body = { sleepBedAt: b, sleepWakeAt: w };
      } else if (hasManual) {
        body = { sleepBedAt: null, sleepWakeAt: null, sleepHours: Number(sleepHoursManual) };
      } else {
        body = { sleepBedAt: null, sleepWakeAt: null, sleepHours: null };
      }
      await api(`/api/nutrition/day/${dateKey}/wellness`, { method: 'PUT', body: JSON.stringify(body) });
      setNote('Saved');
      setFormOpen(false);
      await refresh();
    } catch {
      setNote('Could not save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 pb-2">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-white">Sleep</h1>
          <p className="mt-0.5 text-xs text-zinc-500">{dateKey}</p>
        </div>
        <input
          type="date"
          value={dateKey}
          onChange={(e) => setDateKey(e.target.value)}
          className={`${fieldControl} !w-auto !py-2 text-xs`}
        />
      </div>

      <PremiumCard variant="hero">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <StatRing
              value={hours ?? 0}
              max={8}
              label="Hours"
              display={hasLog ? String(hours) : '—'}
              size={88}
            />
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Score</div>
              <div className="font-display text-3xl font-bold tabular-nums text-white">
                {hasLog ? sleepScore(hours) : '—'}
              </div>
            </div>
          </div>
          {!formOpen ? (
            <ActionButton onClick={() => setFormOpen(true)}>{hasLog ? 'Edit' : 'Add sleep'}</ActionButton>
          ) : null}
        </div>

        {hasLog && day?.sleepBedAt && day?.sleepWakeAt ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="rounded-xl border border-violet-500/20 bg-violet-500/10 px-3 py-2 text-xs text-violet-100">
              Bed {formatTimeChip(day.sleepBedAt)}
            </span>
            <span className="rounded-xl border border-violet-500/20 bg-violet-500/10 px-3 py-2 text-xs text-violet-100">
              Wake {formatTimeChip(day.sleepWakeAt)}
            </span>
          </div>
        ) : null}
      </PremiumCard>

      {!hasLog && !formOpen ? (
        <PremiumEmpty
          icon={<NavIcon name="sleep" className="h-6 w-6" />}
          title="Log your sleep"
          onAction={() => setFormOpen(true)}
          actionLabel="Add sleep"
        />
      ) : null}

      <CollapsiblePanel title="Log sleep" open={formOpen} onOpenChange={setFormOpen}>
        <PremiumCard className="!p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <fieldset className="min-w-0 border-0 p-0">
              <legend className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Bed</legend>
              <div className="flex gap-2">
                <input type="date" value={bedDate} onChange={(e) => setBedDate(e.target.value)} className={sleepInputClass} />
                <input type="time" step={60} value={bedTime} onChange={(e) => setBedTime(e.target.value)} className={sleepInputClass} />
              </div>
            </fieldset>
            <fieldset className="min-w-0 border-0 p-0">
              <legend className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Wake</legend>
              <div className="flex gap-2">
                <input type="date" value={wakeDate} onChange={(e) => setWakeDate(e.target.value)} className={sleepInputClass} />
                <input type="time" step={60} value={wakeTime} onChange={(e) => setWakeTime(e.target.value)} className={sleepInputClass} />
              </div>
            </fieldset>
          </div>
          <label className="mt-4 block text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
            Or hours only
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={sleepHoursManual}
              onChange={(e) => setSleepHoursManual(e.target.value)}
              placeholder="7.5"
              className={`${sleepInputClass} mt-1 max-w-xs`}
            />
          </label>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <ActionButton disabled={saving} onClick={() => void saveSleep()}>
              {saving ? 'Saving…' : 'Save'}
            </ActionButton>
            {note ? <span className="text-sm text-zinc-500">{note}</span> : null}
          </div>
        </PremiumCard>
      </CollapsiblePanel>

      <Link to="/day-review" className="inline-block text-xs font-semibold text-accent">
        Day review →
      </Link>
    </div>
  );
}
